import { FormEvent, useEffect, useState } from "react";

import { ProProjectDialog } from "@/features/pro-buyer-projects/components/ProProjectDialog";
import { useProProjectsCopy } from "@/features/pro-buyer-projects/hooks/useProProjectsCopy";
import { PRO_PROJECT_NAME_MAX_LENGTH } from "@/features/pro-buyer-projects/static/pro-buyer-projects.static";
import {
  getProjectNameErrorCopyKey,
  getProjectNameLengthErrorKey,
  normalizeProjectName,
} from "@/features/pro-buyer-projects/utils/pro-projects.utils";
import { Button } from "@/ui/cva/Button";

type ProProjectNameDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  initialName?: string;
  /** "Project maken" when creating, "Opslaan" when renaming (spec S4). */
  submitLabel: string;
  /** Resolves on success; a rejection is mapped to the gateway error copy. */
  onSubmit: (name: string) => Promise<unknown>;
};

const NAME_INPUT_ID = "pro-project-name";
const NAME_ERROR_ID = "pro-project-name-error";
const NAME_HELP_ID = "pro-project-name-help";

/** WHO-5959 6b. Create and rename share one form: 1 to 40 characters, unique, no e-mail address or link. */
export const ProProjectNameDialog = ({
  open,
  onOpenChange,
  title,
  initialName = "",
  submitLabel,
  onSubmit,
}: ProProjectNameDialogProps) => {
  const { tc } = useProProjectsCopy();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
    }
  }, [open, initialName]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const lengthError = getProjectNameLengthErrorKey(name);

    if (lengthError) {
      setError(tc(lengthError));

      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit(normalizeProjectName(name));
      onOpenChange(false);
    } catch (submitError) {
      setError(tc(getProjectNameErrorCopyKey(submitError)));
    } finally {
      setSubmitting(false);
    }
  };

  const length = [...name].length;

  return (
    <ProProjectDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button
            type="button"
            color="brand"
            appearance="outlined"
            size="medium"
            onClick={() => onOpenChange(false)}
          >
            {tc("cancel")}
          </Button>
          <Button
            type="submit"
            form="pro-project-name-form"
            color="brand"
            appearance="solid"
            size="medium"
            disabled={submitting}
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <form
        id="pro-project-name-form"
        onSubmit={handleSubmit}
        className="flex flex-col space-y-2"
        noValidate
      >
        <label htmlFor={NAME_INPUT_ID} className="font-title text-sm font-bold">
          {tc("nameLabel")}
        </label>
        <input
          id={NAME_INPUT_ID}
          value={name}
          maxLength={PRO_PROJECT_NAME_MAX_LENGTH}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={
            error ? `${NAME_ERROR_ID} ${NAME_HELP_ID}` : NAME_HELP_ID
          }
          onChange={event => setName(event.target.value)}
          className="w-full rounded-[6px] border border-grey-300 px-4 py-3 font-body text-base focus:border-brand-500 focus:outline-none"
        />
        <p id={NAME_HELP_ID} className="font-body text-sm text-grey-600">
          {tc("nameHelp")}
        </p>
        <div className="flex justify-between gap-3 font-body text-sm">
          <span id={NAME_ERROR_ID} role="alert" className="text-attention-500">
            {error}
          </span>
          <span className="shrink-0 text-grey-600">
            {length}/{PRO_PROJECT_NAME_MAX_LENGTH}
          </span>
        </div>
      </form>
    </ProProjectDialog>
  );
};
